import React, { useRef } from 'react'
import { css } from '@emotion/core'
import { trackEvent } from '@fs/zion-analytics'
import { string } from 'prop-types'
import { HeaderBlock, Grid, Cell, Card, useAtSize, Spinner, Button, useStatusOverlay } from '@fs/zion-ui'
import { MenuClose, ContentAdd } from '@fs/zion-icon'
import { useUser } from '@fs/zion-user'
import { useTranslation } from 'react-i18next'
import AncestorGallery from '../../../../../../wcf/AppWcf/components/AncestorGallery/AncestorGallery'
import filterMemories from '../../../../../helpers/memory_helper'
import { usePostPhotoMemories, useDeleteMemory } from '../../../hooks/postHooks'
import useMemories from '../../../../../hooks/useMemories'
import DSRenderWithMode from '../../../../../helpers/DSRenderWithMode'
import handleFileUploadAttempt from '../../../../../helpers/upload'
import DeleteMemoryButton from '../../../../DeleteMemoryButton/DeleteMemoryButton'
import { usePersonViewerState, usePersonViewerDispatch } from '../../../state/PersonViewerStore'

const cssImage = css`
  width: 100%;
  height: 275px;
  outline: none;
  margin-top: 16px;

  object-fit: cover;
  object-position: center top;
  &:hover {
    cursor: pointer;
  }
`

const cssAddMemory = css`
  border: 4px dotted #e0e0e0;
  margin: 10px 20px;
  cursor: pointer;
  font-size: 14px;
  color: #999;
  vertical-align: top;
  width: 200px;
  height: 200px;
  align-self: center;
  background-color: #fff;
`

const imageFormCSS = css`
  display: none;
`

const cssCloseButton = css`
  position: fixed;
  top: 10px;
  right: 10px;
  background: transparent;
  border: 0;
  z-index: 1600;
`

const photoGridContainerCSS = css`
  margin: 20px;
`

const spinnerContainer = css`
  margin: auto 0;
`
const PhotoForm = ({ uploadPhotoForm, uploadTrigger, handlePhotoUploadOnChange, personId }) => (
  <div css={imageFormCSS}>
    <form ref={uploadPhotoForm} method="post" name="photoForm">
      <input
        ref={uploadTrigger}
        name="image"
        onChange={handlePhotoUploadOnChange}
        type="file"
        accept=".jpg,.bmp,.png,.pdf"
        id="image"
      />
      <input name="ancestorId" className="ancestor-id" type="hidden" value={personId} />
    </form>
  </div>
)

export default function PersonPhotos({ personId, gender, centerOrStation }) {
  const [t] = useTranslation()
  const user = useUser()
  const memoryType = 'photo'
  const [isLoading, setIsLoading] = React.useState(true)
  const [memories, setMemories] = React.useState([])
  const [showGallery, setShowGallery] = React.useState(false)
  const [imageIndex, setImageIndex] = React.useState()
  const uploadPhotoForm = useRef(null)
  const uploadTrigger = useRef(null)
  const atSize = useAtSize()
  const [newPhoto, postNewPhoto] = usePostPhotoMemories()
  const [memoriesState, dispatch] = useMemories({ personId })
  const [oldMemory, deleteMemory] = useDeleteMemory()
  const showStatusOverlay = useStatusOverlay()
  const personViewerState = usePersonViewerState()
  const { lockMemories } = personViewerState
  const personViewerDispatch = usePersonViewerDispatch()

  const banner = css`
    width: 80%;
    background-color: #fff;
    text-align: center;
    margin: 0 auto;
    padding-bottom: 5px;
    margin-top: ${atSize({ default: '75px', xxl: '200px' })};
  `

  const bannerBtn = css`
    display: 'flex';
    flex-direction: 'column';
    align-items: 'center';
    margin-top: 20px;
  `

  const uploadPhoto = () => {
    uploadTrigger.current.click()
    trackEvent({ event_name: 'Upload Photo' })
  }

  React.useEffect(() => {
    if (memoriesState.complete && memoriesState.res && memoriesState.res.sourceDescriptions) {
      const images = filterMemories({ memories: memoriesState.res, memoryType: 'image' })
      setMemories(images)
      setIsLoading(false)
    } else if (memoriesState.complete && memoriesState.res === '') {
      setIsLoading(false)
    }
  }, [memoriesState])

  React.useEffect(() => {
    if (newPhoto.complete && !newPhoto.error && !newPhoto.pending) {
      showStatusOverlay({
        message: t(`person-details.upload-successful-${memoryType}`),
        type: 'success',
        dismissable: true,
      })
      dispatch({ type: 'load' })
    }
  }, [dispatch, newPhoto, showStatusOverlay, t])

  React.useEffect(() => {
    if (newPhoto.pending) {
      showStatusOverlay({
        message: t(`person-details.uploading-memory-${memoryType}`),
        type: 'normal',
        dismissable: true,
      })
    }
  }, [newPhoto.pending, showStatusOverlay, t])

  React.useEffect(() => {
    if (newPhoto.complete && newPhoto.error && !newPhoto.pending) {
      showStatusOverlay({
        message: t(
          `person-details.status-${newPhoto.error?.response?.status || 'error'}${
            newPhoto.error?.response?.status === 409 ? `-${memoryType}` : ''
          }`
        ),
        type: 'error',
        dismissable: true,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newPhoto])

  React.useEffect(() => {
    if (oldMemory.complete && !oldMemory.error && !oldMemory.pending) {
      dispatch({ type: 'load' })
    }
  }, [dispatch, oldMemory])

  const handlePhotoSubmit = () => {
    const data = new FormData(uploadPhotoForm.current)
    postNewPhoto({
      ancestorId: personId,
      data,
    })
  }

  const handlePhotoUploadOnChange = () => {
    handleFileUploadAttempt({
      fileForm: uploadPhotoForm.current,
      fileInputName: 'image',
      uploadCallback: handlePhotoSubmit,
      fileTooLargeHandler: () => {
        showStatusOverlay({
          message: t('person-details.file-too-large', 'The file is too large.  The size limit is 15 megabytes.'),
          type: 'error',
        })
      },
    })
  }

  const openDialog = (index) => {
    setImageIndex(index)
    setShowGallery(true)
    personViewerDispatch({
      type: 'SET_TAB_CONTENT_OVERFLOW',
      data: {
        tabContentOverflow: 'visible',
      },
    })
  }

  const closeDialog = () => {
    setShowGallery(false)
    personViewerDispatch({
      type: 'SET_TAB_CONTENT_OVERFLOW',
      data: {
        tabContentOverflow: 'auto',
      },
    })
  }

  const photoFormProps = {
    uploadPhotoForm,
    uploadTrigger,
    handlePhotoUploadOnChange,
    personId,
  }

  const ImageCards = ({ image, index }) => {
    const [imageSource, setImageSource] = React.useState()

    React.useEffect(() => {
      const img = document.createElement('img')
      img.src = image.thumbnail
      img.onload = () => setImageSource(image.thumbnail)
    }, [image, setImageSource])

    return imageSource ? (
      <Cell columns={atSize({ default: 12, sm: 6, lg: 4, xl: 3, xxl: 2 })} key={image.thumbnail}>
        <Card
          data-index={index}
          onClick={() => openDialog(index)}
          onKeyPress={() => openDialog(index)}
          role="button"
          tabIndex={-1}
        >
          <Card.Body>
            <img
              css={cssImage}
              src={imageSource}
              loading="lazy"
              width="250px"
              height="275px"
              alt={t('person-details.screening', 'Screening...')}
            />
          </Card.Body>
          {image.resourceId === user.cisId && !lockMemories && !centerOrStation && (
            <Card.Actions>
              <DeleteMemoryButton artifactId={image.id} deleteMemory={deleteMemory} />
            </Card.Actions>
          )}
        </Card>
      </Cell>
    ) : (
      <></>
    )
  }

  return (
    <>
      {showGallery ? (
        <div>
          <AncestorGallery imageList={memories.map((image) => image.full)} index={imageIndex} />
          <button css={cssCloseButton} onClick={closeDialog} data-dismiss="modal" aria-label="Close" type="button">
            <MenuClose size="sm" color="rgba(255,255,255,0.6)" />
          </button>
        </div>
      ) : null}
      {!isLoading && memories.length > 0 && (
        <div>
          <PhotoForm {...photoFormProps} />
          <div>
            <div css={photoGridContainerCSS}>
              <Grid>
                {!lockMemories && !centerOrStation && (
                  <DSRenderWithMode allowedModes={['WEB']}>
                    <Cell columns={atSize({ default: 12, sm: 6, lg: 4, xl: 3, xxl: 2 })}>
                      <button css={cssAddMemory} type="button" onClick={uploadPhoto}>
                        <ContentAdd size="xl" color="#aaa" />
                        <div>{t('person-details.add-photo', 'Add Photo')}</div>
                      </button>
                    </Cell>
                  </DSRenderWithMode>
                )}
                {memories.map((image, index) => (
                  <ImageCards image={image} index={index} key={image.thumbnail} />
                ))}
                {(newPhoto.pending || oldMemory.pending) && (
                  <Cell columns={atSize({ default: 12, sm: 6, lg: 4, xl: 3, xxl: 2 })}>
                    <div css={spinnerContainer}>
                      <Spinner large />
                    </div>
                  </Cell>
                )}
              </Grid>
            </div>
          </div>
        </div>
      )}

      {!isLoading && memories.length === 0 && (
        <div css={banner}>
          {lockMemories && <HeaderBlock size="md" heading={t('person-details.no-records', 'No records found')} />}
          {centerOrStation && (
            <HeaderBlock
              size="md"
              heading={t('person-details.be-first-center', 'Be the first to add to this record on FamilySearch.org')}
            />
          )}
          {!lockMemories && !centerOrStation && (
            <div>
              <PhotoForm {...photoFormProps} />
              <HeaderBlock size="md" heading={t('person-details.be-first', 'Be the first to add to this record')} />
              <div css={bannerBtn}>
                <Button emphasis="billboard" color={gender && gender === 'male' ? 'blue' : 'red'} onClick={uploadPhoto}>
                  {t('person-details.add-photo', 'Add Photo')}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
      {isLoading && memories.length === 0 && (
        <div css={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '500px' }}>
          <Spinner large />
        </div>
      )}
    </>
  )
}

PersonPhotos.propTypes = {
  personId: string,
}
